import RelatorChip from '@/components/shared/RelatorChip';
import { SITUACAO_IMPEDIMENTOS_PROCESSO } from '@/dominio/SituacaoImpedimentosProcesso';
import { SITUACAO_VOTO } from '@/dominio/SituacaoVoto';
import VotacaoBusiness from '@/dominio/VotacaoBusiness';
import ErrorUtil from '@/util/ErrorUtil';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HowToVote from '@mui/icons-material/HowToVote';
import InfoIcon from '@mui/icons-material/Info';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import PanToolIcon from '@mui/icons-material/PanTool';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import { blue } from '@mui/material/colors';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import ReactComponent from '../../ReactComponent';

class PainelVotacao extends ReactComponent {
    aberto = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            aberto: observable,
            abrir: action,
            fechar: action,
            renderTituloDialog: computed,
            composicaoComVotos: computed,
            renderVotos: computed
        });

        this.createReaction(
            reaction(
                () => this.aberto,
                () => {
                    if (this.aberto) {
                        try {
                            this.fotoMagistradoStore.carregarFotosPelaComposicaoProcesso(
                                this.observableProps.registro.composicao
                            );
                        } catch (error) {
                            this.mensagemStore.abrirMensagemErro(ErrorUtil.getMensagemErro(error));
                        }
                    }
                }
            )
        );
    }

    abrir = () => {
        this.aberto = true;
    };

    fechar = () => {
        this.aberto = false;
    };

    get renderTituloDialog() {
        return (
            <Stack direction="row">
                <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-start' }}>
                    <Typography
                        display="inline"
                        sx={{ fontWeight: 'bold' }}
                    >
                        Painel de votação
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
                    <Typography
                        display="inline"
                        sx={{ fontWeight: 'bold' }}
                    >
                        {this.observableProps.registro.processo.numeroCompleto}
                    </Typography>
                    <RelatorChip
                        relator={this.observableProps.registro.processo.relator}
                        composicao={this.observableProps.registro.composicao}
                        exibirFoto={true}
                        sx={{ ml: 1 }}
                    />
                </Box>
            </Stack>
        );
    }

    /**
     * @returns {Array} Objeto que contém a composição da processo com os votos
     */
    get composicaoComVotos() {
        return this.sessaoStore.montarComposicaoComVotos(this.observableProps.registro);
    }

    /**
     * @param {string} sigla A sigla do magistrado
     * @returns {Object | null} O objeto que representa um magistrado
     */
    getObjMagistradoComposicaoPelaSigla(sigla) {
        if (!this.observableProps.registro?.composicao) {
            return null;
        }

        return this.observableProps.registro.composicao.find(
            (/** @type {Object} */ comp) => comp.magistrado.sigla === sigla
        );
    }

    renderTooltipMagistrado(siglaMagistrado) {
        const magistradoComposicao = this.getObjMagistradoComposicaoPelaSigla(siglaMagistrado);
        const nomeMagistrado = magistradoComposicao?.magistrado?.nome;
        const url = magistradoComposicao?.imagem?.url
            ? magistradoComposicao.imagem.url
            : magistradoComposicao?.magistrado?.cpf;

        const contentTypePadrao = 'jpeg';
        let foto = null;

        if (url) {
            foto = this.fotoMagistradoStore.fotosDisponiveisEmMemoria.get(url);
        }

        if (!foto?.imgBase64) {
            return <Typography>{nomeMagistrado || siglaMagistrado}</Typography>;
        }

        const srcFoto = `data:image/${foto.imgContentType ? foto.imgContentType : contentTypePadrao};base64,${foto.imgBase64}`;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    m: 1
                }}
            >
                <Avatar
                    sx={{ width: 120, height: 120 }}
                    src={srcFoto}
                />
                <Typography sx={{ mt: 1 }}>{nomeMagistrado || siglaMagistrado}</Typography>
            </Box>
        );
    }

    isPreVoto(voto) {
        return VotacaoBusiness.isPreVoto(voto);
    }

    getDescricaoVoto(voto) {
        return VotacaoBusiness.getDescricaoVoto(voto);
    }

    getCorVoto(voto) {
        return VotacaoBusiness.getCorVoto(voto);
    }

    getObjDetalhesNaoVotante(mag) {
        if (!mag) {
            return null;
        }

        let cor;
        let icone;
        let descricao;

        if (mag.relator) {
            cor = 'blue';
            icone = <EditNoteIcon sx={{ ml: 1, color: cor }} />;
            descricao = 'Relator';
        } else if (mag.ausente) {
            cor = SITUACAO_VOTO.SEM_VOTO.cor;
            icone = <LocationOffIcon sx={{ ml: 1, color: cor }} />;
            descricao = 'Magistrado ausente nesta sessão';
        } else if (mag.impedido || mag.possivelmenteImpedido) {
            if (mag.impedido && mag.possivelmenteImpedido) {
                icone = (
                    <Stack direction="row">
                        <PanToolIcon sx={{ ml: 1, color: cor }} />;
                        <ThumbDownIcon
                            sx={{
                                transform: 'scaleX(-1)',
                                ml: 1,
                                color: cor
                            }}
                        />
                    </Stack>
                );
                descricao = 'Magistrado com impedimentos';
            } else if (mag.impedido) {
                cor = SITUACAO_IMPEDIMENTOS_PROCESSO.IMPEDIMENTO_DECLARADO_NA_COMPOSICAO.cor;
                icone = <PanToolIcon sx={{ ml: 1, color: cor }} />;
                descricao = 'Magistrado impedido';
            } else {
                cor = SITUACAO_IMPEDIMENTOS_PROCESSO.POSSIVEL_IMPEDIMENTO_NA_COMPOSICAO.cor;
                icone = (
                    <ThumbDownIcon
                        sx={{
                            transform: 'scaleX(-1)',
                            ml: 1,
                            color: cor
                        }}
                    />
                );
                descricao = 'Magistrado possivelmente impedido';
            }
        } else {
            cor = SITUACAO_VOTO.SEM_VOTO.cor;
            icone = <CloseIcon sx={{ ml: 1, color: cor }} />;
            descricao = 'Sem registro de voto';
        }

        return { descricao, cor, icone };
    }

    renderDescricaoNaoVotante(mag) {
        const objNaoVotante = this.getObjDetalhesNaoVotante(mag);

        return (
            <Stack direction="row">
                {objNaoVotante.icone}
                <Typography sx={{ ml: 1, color: objNaoVotante.cor }}>{objNaoVotante.descricao}</Typography>
            </Stack>
        );
    }

    get renderVotos() {
        if (!this.observableProps.registro.votos || !this.composicaoComVotos) {
            return (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <InfoIcon
                        fontSize="large"
                        sx={{ color: blue[800] }}
                    />
                    <Typography sx={{ ml: 2, color: blue[800] }}>Não há votos registrados.</Typography>
                </Box>
            );
        }

        // [FIXME] Exibir votos fora da composição

        return (
            <Box sx={{ mt: 2 }}>
                {this.composicaoComVotos.map((mag) => {
                    const detalhesVoto = VotacaoBusiness.getObjDetalhesVoto(mag.voto);

                    return (
                        <Box
                            key={mag.siglaDoMagistrado}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                pt: 2,
                                pr: 2,
                                pb: 1,
                                borderBottom: '2px solid black'
                            }}
                        >
                            <Tooltip
                                placement="right-start"
                                title={this.renderTooltipMagistrado(mag.siglaDoMagistrado)}
                            >
                                <Typography
                                    display={'inline'}
                                    sx={{ fontWeight: 'bold', width: '80px' }}
                                >
                                    {mag.siglaDoMagistrado}
                                </Typography>
                            </Tooltip>
                            <Typography display={'inline'}>:</Typography>
                            {detalhesVoto ? (
                                <>
                                    <HowToVote sx={{ ml: 1, color: detalhesVoto.cor }} />
                                    <Typography
                                        sx={{ ml: 1 }}
                                        color={detalhesVoto.cor}
                                    >
                                        {detalhesVoto.descricao}
                                    </Typography>
                                    {detalhesVoto.podeExibir && detalhesVoto.situacao && (
                                        <Tooltip
                                            title={<Typography>{detalhesVoto.situacao.dataConfirmacao}</Typography>}
                                        >
                                            <Typography
                                                display={'inline'}
                                                sx={{
                                                    ml: 1,
                                                    fontWeight: 'bold',
                                                    color: detalhesVoto.situacao.cor
                                                }}
                                            >
                                                {` (${detalhesVoto.situacao.descricao})`}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </>
                            ) : (
                                this.renderDescricaoNaoVotante(mag)
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    }

    render() {
        if (!this.isComponenteHabilitado('PainelVotacao')) {
            return null;
        }

        return (
            <>
                <IconButton
                    onClick={this.abrir}
                    sx={{ cursor: 'pointer' }}
                    size="small"
                >
                    <Tooltip title={<Typography>Painel de Votação</Typography>}>
                        <HowToVote
                            sx={{
                                color: 'black'
                            }}
                        />
                    </Tooltip>
                </IconButton>
                {this.aberto && (
                    <Dialog
                        open={this.aberto}
                        onClose={this.fechar}
                        maxWidth={'md'}
                        fullWidth
                    >
                        <DialogTitle sx={{ pt: 3, pl: 3, pr: 5 }}>{this.renderTituloDialog}</DialogTitle>
                        <DialogContent>{this.renderVotos}</DialogContent>
                        <DialogActions sx={{ pr: 5, pb: 3 }}>
                            <Button
                                color="primary"
                                variant="contained"
                                endIcon={<CloseIcon />}
                                onClick={this.fechar}
                            >
                                Fechar
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    }
}

export default inject('stores')(observer(PainelVotacao));
